import React, { useState, useEffect } from 'react';
import Popover from '@mui/material/Popover';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

interface ImageSelectorPopoverProps {
  id: string | undefined;
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  onSelect: (imageFilename: string) => void;
  currentImage?: string | null;
}

const availableImages = [
  'mentor-service-1.png',
  'mentor-service-2.png',
  'mentor-service-3.png',
];

const ImageSelectorPopover: React.FC<ImageSelectorPopoverProps> = ({
  id,
  open,
  anchorEl,
  onClose,
  onSelect,
  currentImage,
}) => {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setSelectedImage(currentImage || null);
    }
  }, [open, currentImage]);

  const handleImageClick = (imageFilename: string) => {
    setSelectedImage(imageFilename);
  };

  const handleSave = () => {
    if (selectedImage) {
      onSelect(selectedImage);
    }
    onClose();
  };

  return (
    <Popover
      id={id}
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
    >
      <div className="p-6 w-full max-w-md bg-white rounded-[20px] shadow-[0px_7px_29px_0px_rgba(100,100,111,0.20),0px_14px_42px_0px_rgba(8,15,52,0.06)] outline-none"> 
        <div className="flex justify-between items-center pb-4 mb-4 border-b">
          <h2 className="text-xl font-semibold text-gray-800">
            Change Image
          </h2>
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{ padding: '4px' }}
          >
            <CloseIcon />
          </IconButton>
        </div>

        <div className="grid grid-cols-3 gap-4 mb-6">
          {availableImages.map((imgFilename) => (
            <div
              key={imgFilename}
              className={`relative cursor-pointer rounded-lg border-2 p-2 transition-all duration-200 ease-in-out ${
                selectedImage === imgFilename ? 'border-blue-500 scale-105' : 'border-transparent hover:border-gray-300'
              }`}
              onClick={() => handleImageClick(imgFilename)}
            >
              <img
                src={require(`assets/${imgFilename}`)}
                alt={imgFilename}
                className="object-contain w-full h-auto rounded"
              />
              {selectedImage === imgFilename && (
                <CheckCircleIcon
                  className="absolute top-1 right-1 text-blue-500 bg-white rounded-full"
                  style={{ fontSize: '1.5rem' }}
                />
              )}
            </div>
          ))}
        </div>

        <div className="flex justify-end pt-4 border-t">
          <button
            onClick={handleSave}
            disabled={!selectedImage}
            className="rounded-lg bg-[#407EC9] px-6 py-2 text-white transition-colors custom-text-[poppins-500-16px] hover:bg-[#3569A7] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Save
          </button>
        </div>
      </div>
    </Popover>
  );
};

export default ImageSelectorPopover;
